import React, { useState, useEffect, useCallback } from 'react'
import { quickDeskSettingsApi, LINE_ITEMS, LINE_ITEM_LABELS, ITEM_SUFFIXES } from '../../services/quickDeskSettings'
import type { QuickDeskSetting, QuickDeskDisclaimer } from '../../services/quickDeskSettings'
import { Toast } from '../shared/Toast'

const TAX_APPLY_OPTIONS = [
  { value: 0, label: 'None' },
  { value: 1, label: 'Option 1' },
  { value: 2, label: 'Option 2' },
  { value: 3, label: 'Option 3' },
  { value: 4, label: 'Option 4' },
  { value: 5, label: 'Option 5' },
  { value: 6, label: 'Option 6' },
  { value: 7, label: 'Option 7' },
]

const SUFFIX_LABELS: Record<string, string> = {
  amnt: 'Amount',
  label: 'Label',
  tax_1: 'Tax 1 Rate',
  tax_2: 'Tax 2 Rate',
  tax_credit: 'Tax Credit',
  tax_apply_to: 'Tax Apply To',
  tax_credit_apply_to: 'Tax Credit Apply To',
}

export function QuickDeskSettings(): React.ReactElement | null {
  const [setting, setSetting] = useState<QuickDeskSetting | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null)
  const [hasChanges, setHasChanges] = useState(false)

  const [formExpiration, setFormExpiration] = useState<number | null>(null)
  const [formLineItems, setFormLineItems] = useState<Record<string, unknown>>({})
  const [formDisclaimers, setFormDisclaimers] = useState<QuickDeskDisclaimer[]>([])
  const [expandedItem, setExpandedItem] = useState<string | null>(null)

  const showToast = useCallback((message: string, type: 'success' | 'error') => {
    setToast({ message, type })
  }, [])

  const syncFormState = useCallback((s: QuickDeskSetting) => {
    setFormExpiration(s.expiration)
    setFormDisclaimers(s.disclaimers.map(d => ({ ...d })))

    const lineItemData: Record<string, unknown> = {}
    for (const item of LINE_ITEMS) {
      for (const suffix of ITEM_SUFFIXES) {
        const key = `${item}_${suffix}`
        lineItemData[key] = s[key] ?? (suffix === 'label' ? '' : 0)
      }
    }
    setFormLineItems(lineItemData)
    setHasChanges(false)
  }, [])

  const loadSettings = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await quickDeskSettingsApi.show()
      setSetting(data)
      syncFormState(data)
    } catch {
      setError('Failed to load Quick Desk settings. Please try again.')
    } finally {
      setLoading(false)
    }
  }, [syncFormState])

  useEffect(() => {
    loadSettings()
  }, [loadSettings])

  const markChanged = () => setHasChanges(true)

  const handleLineItemChange = (key: string, value: unknown) => {
    setFormLineItems(prev => ({ ...prev, [key]: value }))
    markChanged()
  }

  const handleAddDisclaimer = () => {
    setFormDisclaimers(prev => [...prev, { title: '', disclaimer_text: '' }])
    markChanged()
  }

  const handleRemoveDisclaimer = (index: number) => {
    setFormDisclaimers(prev => {
      const updated = [...prev]
      const disclaimer = updated[index]
      if (disclaimer.id) {
        updated[index] = { ...disclaimer, _destroy: true }
      } else {
        updated.splice(index, 1)
      }
      return updated
    })
    markChanged()
  }

  const handleDisclaimerChange = (index: number, field: 'title' | 'disclaimer_text', value: string) => {
    setFormDisclaimers(prev => {
      const updated = [...prev]
      updated[index] = { ...updated[index], [field]: value }
      return updated
    })
    markChanged()
  }

  const handleSave = async () => {
    try {
      setSaving(true)
      const params: Record<string, unknown> = {
        expiration: formExpiration,
        disclaimers_attributes: formDisclaimers,
        ...formLineItems,
      }
      const updated = await quickDeskSettingsApi.update(params)
      setSetting(updated)
      syncFormState(updated)
      showToast('Quick Desk settings saved', 'success')
    } catch {
      showToast('Failed to save Quick Desk settings', 'error')
    } finally {
      setSaving(false)
    }
  }

  const toggleAccordion = (item: string) => {
    setExpandedItem(prev => prev === item ? null : item)
  }

  // Loading state
  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-12">
        <div className="flex items-center justify-center gap-3 text-grey">
          <div className="w-5 h-5 border-2 border-gray-300 border-t-primary rounded-full animate-spin" />
          <span className="text-sm">Loading Quick Desk settings...</span>
        </div>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
        <div className="w-12 h-12 bg-red-50 rounded-xl flex items-center justify-center mx-auto mb-4">
          <svg className="w-6 h-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
          </svg>
        </div>
        <p className="text-sm text-grey mb-4">{error}</p>
        <button onClick={loadSettings} className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary/90">
          Try Again
        </button>
      </div>
    )
  }

  if (!setting) return null

  const visibleDisclaimers = formDisclaimers.filter(d => !d._destroy)

  return (
    <div className="space-y-6">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-secondary">Quick Desk Settings</h1>
          <p className="text-sm text-grey mt-1">Configure Quick Desk calculator line items and disclaimers.</p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving || !hasChanges}
          className="inline-flex items-center gap-2 px-5 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {saving && <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />}
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      {/* Expiration */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100">
          <h2 className="text-sm font-semibold text-secondary">Expiration</h2>
        </div>
        <div className="p-6">
          <div>
            <label htmlFor="expiration" className="block text-sm font-medium text-secondary mb-1.5">
              Expiration (days)
            </label>
            <input
              id="expiration"
              type="number"
              min={0}
              value={formExpiration ?? ''}
              onChange={(e) => { setFormExpiration(e.target.value ? parseInt(e.target.value, 10) : null); markChanged() }}
              placeholder="Number of days"
              className="w-48 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none"
            />
            <p className="text-xs text-grey mt-1.5">Number of days before Quick Desk quotes expire.</p>
          </div>
        </div>
      </div>

      {/* Line Items */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100">
          <h2 className="text-sm font-semibold text-secondary">Line Items</h2>
          <p className="text-xs text-grey mt-0.5">Configure each line item for the Quick Desk calculator.</p>
        </div>
        <div className="divide-y divide-gray-100">
          {LINE_ITEMS.map(item => {
            const isExpanded = expandedItem === item
            const itemLabel = LINE_ITEM_LABELS[item] || item

            return (
              <div key={item}>
                <button
                  type="button"
                  onClick={() => toggleAccordion(item)}
                  className="w-full flex items-center justify-between px-6 py-3 text-left hover:bg-gray-50 transition-colors"
                  aria-expanded={isExpanded}
                >
                  <span className="text-sm font-medium text-secondary">{itemLabel}</span>
                  <svg
                    className={`w-4 h-4 text-grey transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                  </svg>
                </button>

                {isExpanded && (
                  <div className="px-6 pb-5 pt-2 bg-gray-50/50">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {ITEM_SUFFIXES.map(suffix => {
                        const key = `${item}_${suffix}`
                        const label = SUFFIX_LABELS[suffix] || suffix
                        const isSelect = suffix === 'tax_apply_to' || suffix === 'tax_credit_apply_to'
                        const isText = suffix === 'label'

                        return (
                          <div key={key}>
                            <label htmlFor={key} className="block text-xs font-medium text-secondary mb-1">
                              {label}
                            </label>
                            {isSelect ? (
                              <select
                                id={key}
                                value={Number(formLineItems[key] ?? 0)}
                                onChange={(e) => handleLineItemChange(key, parseInt(e.target.value, 10))}
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none bg-white"
                              >
                                {TAX_APPLY_OPTIONS.map(opt => (
                                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                              </select>
                            ) : isText ? (
                              <input
                                id={key}
                                type="text"
                                value={String(formLineItems[key] ?? '')}
                                onChange={(e) => handleLineItemChange(key, e.target.value)}
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none"
                              />
                            ) : (
                              <input
                                id={key}
                                type="number"
                                step="any"
                                value={formLineItems[key] !== undefined && formLineItems[key] !== null ? String(formLineItems[key]) : ''}
                                onChange={(e) => handleLineItemChange(key, e.target.value ? parseFloat(e.target.value) : 0)}
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none"
                              />
                            )}
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>

      {/* Disclaimers */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h2 className="text-sm font-semibold text-secondary">Disclaimers</h2>
            <p className="text-xs text-grey mt-0.5">Add disclaimers to display on Quick Desk quotes.</p>
          </div>
          <button
            type="button"
            onClick={handleAddDisclaimer}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-primary border border-primary rounded-lg hover:bg-primary/5 transition-colors"
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
            </svg>
            Add Disclaimer
          </button>
        </div>
        <div className="p-6">
          {visibleDisclaimers.length === 0 ? (
            <p className="text-sm text-grey text-center py-4">No disclaimers configured. Click "Add Disclaimer" to create one.</p>
          ) : (
            <div className="space-y-4">
              {formDisclaimers.map((disclaimer, index) => {
                if (disclaimer._destroy) return null

                return (
                  <div key={disclaimer.id || `new-${index}`} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-start justify-between gap-4 mb-3">
                      <div className="flex-1">
                        <label htmlFor={`disclaimer-title-${index}`} className="block text-xs font-medium text-secondary mb-1">
                          Title
                        </label>
                        <input
                          id={`disclaimer-title-${index}`}
                          type="text"
                          value={disclaimer.title}
                          onChange={(e) => handleDisclaimerChange(index, 'title', e.target.value)}
                          placeholder="Disclaimer title"
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemoveDisclaimer(index)}
                        className="mt-5 p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                        title="Remove disclaimer"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                        </svg>
                      </button>
                    </div>
                    <div>
                      <label htmlFor={`disclaimer-text-${index}`} className="block text-xs font-medium text-secondary mb-1">
                        Disclaimer Text
                      </label>
                      <textarea
                        id={`disclaimer-text-${index}`}
                        value={disclaimer.disclaimer_text}
                        onChange={(e) => handleDisclaimerChange(index, 'disclaimer_text', e.target.value)}
                        placeholder="Enter disclaimer text..."
                        rows={3}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none resize-y"
                      />
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
